import sendEvent, { resolveExternalId, resolveDonorData } from "@/helpers/sendEvent";

// Browser-side conversion tracking (Meta Pixel + Twitter Pixel).
// Every event carries value/currency so Meta can actually read the parameters.

const DEFAULT_CURRENCY = "KWD";
const LAST_CHECKOUT_KEY = "pixel_last_checkout";
const TRACKED_DONATIONS_KEY = "pixel_tracked_donations";

function buildParams(params = {}) {
  const value = Number(params.value);
  const data = {
    value: isNaN(value) ? 0 : Number(value.toFixed(3)),
    currency: params.currency || DEFAULT_CURRENCY,
  };
  if (params.content_ids && params.content_ids.length) {
    data.content_ids = params.content_ids.map(String);
  }
  if (params.content_name) data.content_name = String(params.content_name);
  if (params.content_type) data.content_type = params.content_type;
  if (params.num_items) data.num_items = Number(params.num_items);
  if (params.payment_method) data.payment_method = String(params.payment_method);
  return data;
}

export function trackPixel(eventName, params = {}, eventId = null, extraData = {}) {
  const data = buildParams(params);
  try {
    const extId = resolveExternalId(extraData);
    if (typeof window.fbq === "function") {
      try {
        const donor = resolveDonorData(extraData);
        const userProps = { external_id: extId };
        if (donor.email) userProps.em = donor.email.toLowerCase().trim();
        if (donor.mobile) userProps.ph = donor.mobile.replace(/[^0-9]/g, "");
        if (donor.first_name) userProps.fn = donor.first_name.toLowerCase().trim();
        if (donor.last_name) userProps.ln = donor.last_name.toLowerCase().trim();
        if (donor.country) userProps.country = donor.country.toLowerCase().trim();
        window.fbq("setUserProperties", "3821026401505480", userProps);
      } catch (e) {}
    }
    if (typeof window.fbq === "function") {
      if (eventId) {
        window.fbq("track", eventName, data, { eventID: String(eventId) });
      } else {
        window.fbq("track", eventName, data);
      }
    }
    if (typeof window.twq === "function") {
      window.twq("track", eventName, data);
    }
  } catch (e) {
    // tracking must never break the donation flow
  }
  return data;
}

export function trackAddToCart(params, eventId = null, extra = {}) {
  return trackPixel("AddToCart", params, eventId, extra);
}

// Fired when the donor submits the donation form, before redirecting to the gateway.
// Remembers the amount so the success page can fall back on it.
export function trackCheckout(params, extra = {}) {
  const extraData = typeof extra === "object" && extra !== null ? extra : { mobile: extra };

  // Always resolve the full donor data so fbc, fbp, email etc are present in CAPI.
  const donor = resolveDonorData(extraData);
  const mergedExtra = { ...donor, ...extraData };

  const ts = Date.now();
  const rnd = Math.random().toString(36).substring(2, 7);
  const initId = mergedExtra.event_id || `init_${ts}_${rnd}`;
  const payInfoId = mergedExtra.payment_info_event_id || `pay_${ts}_${rnd}`;

  // Browser pixel
  const data = trackPixel("InitiateCheckout", params, initId, mergedExtra);
  trackPixel("AddPaymentInfo", params, payInfoId, mergedExtra);

  // Expose generated event IDs on return object so caller can attach to donation API record
  data._eventId = initId;
  data.event_id = initId;
  data.init_event_id = initId;
  data.payment_info_event_id = payInfoId;

  // CAPI (server-side) — send BOTH InitiateCheckout AND AddPaymentInfo
  // so they always have dual browser+server coverage regardless of call site.
  try {
    sendEvent("InitiateCheckout", params.value, params.currency || DEFAULT_CURRENCY, {
      ...mergedExtra,
      event_id: initId,
    });
  } catch (e) {}
  try {
    sendEvent("AddPaymentInfo", params.value, params.currency || DEFAULT_CURRENCY, {
      ...mergedExtra,
      event_id: payInfoId,
    });
  } catch (e) {}

  try {
    sessionStorage.setItem(LAST_CHECKOUT_KEY, JSON.stringify(data));
  } catch (e) {
    //
  }
  return data;
}

// Fired once per completed payment (keyed by invoice/hash so a refresh doesn't double count).
// Sends Donate event via browser pixel AND CAPI.
export function trackDonate(params = {}, uniqueKey = "", eventId = null, extraData = {}) {
  let tracked = [];
  try {
    tracked = JSON.parse(localStorage.getItem(TRACKED_DONATIONS_KEY)) || [];
  } catch (e) {
    tracked = [];
  }
  if (uniqueKey && tracked.includes(uniqueKey)) return null;

  let fallback = {};
  try {
    fallback = JSON.parse(sessionStorage.getItem(LAST_CHECKOUT_KEY)) || {};
  } catch (e) {
    fallback = {};
  }
  const merged = Object.assign({}, fallback, params);
  if (!(Number(params.value) > 0)) merged.value = fallback.value;
  if (!params.currency) merged.currency = fallback.currency;

  const donateEventId = eventId || (uniqueKey ? `don_${uniqueKey}` : null);

  // 1. Donate event — browser pixel
  const data = trackPixel("Donate", merged, donateEventId, extraData);

  // 2. CAPI (server-side) — Donate event. Resolve full donor data
  //    so fbc, email etc are always present for the best possible match quality.
  try {
    const donor = resolveDonorData(extraData);
    sendEvent("Donate", merged.value, merged.currency || DEFAULT_CURRENCY, {
      ...donor,
      ...extraData,
      event_id: donateEventId,
    });
  } catch (e) {}

  try {
    sessionStorage.removeItem(LAST_CHECKOUT_KEY);
    if (uniqueKey) {
      tracked.push(uniqueKey);
      localStorage.setItem(
        TRACKED_DONATIONS_KEY,
        JSON.stringify(tracked.slice(-50))
      );
    }
  } catch (e) {
    //
  }
  return data;
}
